import React, { useState, useEffect } from "react";
import Sidebar from '../Sidebar/SidebarAdmin';
import { Box, Typography, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, LinearProgress } from "@mui/material";
import { database } from "../../firebase";
import { ref, get } from 'firebase/database';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';

const UserManagement = () => {
  // Sidebar state
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const toggleSidebar = () => setIsSidebarOpen(prev => !prev);
  const sidebarWidth = isSidebarOpen ? 250 : 80;

  // Data state
  const [coinbox, setCoinbox] = useState(null);
  const [hopper, setHopper] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const cbRef = ref(database, 'coinbox');
    const hopperRef = ref(database, 'hopper');
    Promise.all([get(cbRef), get(hopperRef)])
      .then(([cbSnap, hSnap]) => {
        if (cbSnap.exists()) setCoinbox(cbSnap.val());
        if (hSnap.exists()) setHopper(hSnap.val());
        setLoading(false);
      })
      .catch(err => { console.error(err); setError('Failed to load data.'); setLoading(false); });
  }, []);

  if (loading) return <Box sx={{ p: 2 }}>Loading...</Box>;
  if (error) return <Box sx={{ p: 2, color: 'error.main' }}>{error}</Box>;

  // Compute totals and percentages
  const { coins = {}, lockers = {}, detailed = {} } = coinbox || {};
  const totalValue = (coins.rentalcoinvalue || 0) + (coins.totalextendvalue || 0) + (coins.totalpenaltyvalue || 0);
  const totalCoinsCount = (coins.rentalcoincount || 0) + (coins.totalextendcoin || 0) + (coins.totalpenaltycoin || 0);
  // hopper is max capacity, so fill percent = current / max
  const fillPercent = hopper > 0 ? Math.min(100, (totalCoinsCount / hopper) * 100) : 0;

  // Pie chart data
  const pieData = [
    { name: 'Rental', value: coins.rentalcoinvalue || 0 },
    { name: 'Extend', value: coins.totalextendvalue || 0 },
    { name: 'Penalty', value: coins.totalpenaltyvalue || 0 }
  ];
  const COLORS = ['#8884d8', '#82ca9d', '#ff7f50'];

  return (
    <Box sx={{ backgroundColor: '#f7f9fc', minHeight: '100vh', pt: 8, position: 'relative' }}>
      <Sidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      <Box sx={{ position: 'fixed', top: '64px', left: { xs: '8px', sm: `calc(${sidebarWidth}px + 16px)` }, marginTop:{ xs: '8px', sm: `-50px` }, right: { xs: '8px', sm: '16px' }, bottom: '16px', backgroundColor: '#cfe8fc', borderRadius: 2, p: { xs: 2, sm: 3 }, overflow: 'auto' }}>
        <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>Coinbox Overview</Typography>

        {/* Hopper Fill Progress Bar: current coins / hopper capacity */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="subtitle1">Hopper Capacity Fill ({totalCoinsCount} / {hopper} coins)</Typography>
          <LinearProgress
            variant="determinate"
            value={fillPercent}
            sx={{ height: 12, borderRadius: 6, mt: 1 }}
          />
        </Box>

        {/* Coin Distribution Pie Chart */}
        <Box sx={{ width: '100%', height: 300, mb: 4 }}>
          <Typography variant="subtitle1" sx={{ textAlign: 'center', mb: 1 }}>Coin Distribution by Value</Typography>
          <ResponsiveContainer>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={100} label>
                {pieData.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
              </Pie>
              <Tooltip formatter={(value) => `₱${value}`} />
            </PieChart>
          </ResponsiveContainer>
        </Box>

        {/* Summary Table */}
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Summary Table</Typography>
        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell align="right">Count</TableCell>
                <TableCell align="right">Value (₱)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {['rental', 'extend', 'penalty'].map(type => {
                const cnt = coins[`${type}coincount`] || 0;
                const val = coins[`${type}coinvalue`] || 0;
                return (
                  <TableRow key={type}>
                    <TableCell>{type.charAt(0).toUpperCase() + type.slice(1)}</TableCell>
                    <TableCell align="right">{cnt}</TableCell>
                    <TableCell align="right">₱{val}</TableCell>
                  </TableRow>
                );
              })}
              <TableRow>
                <TableCell><b>Total</b></TableCell>
                <TableCell align="right"><b>{totalCoinsCount}</b></TableCell>
                <TableCell align="right"><b>₱{totalValue}</b></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Per-Locker Totals Table */}
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Per-Locker Totals</Typography>
        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Locker</TableCell>
                <TableCell align="right">Rental</TableCell>
                <TableCell align="right">Extend</TableCell>
                <TableCell align="right">Penalty</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(lockers).map(([locker, stats]) => {
                const cnt = (stats.rentalcoincount || 0) + (stats.extendcoin || 0) + (stats.penaltycoin || 0);
                return (
                  <TableRow key={locker}>
                    <TableCell>{locker}</TableCell>
                    <TableCell align="right">{stats.rentalcoincount || 0}</TableCell>
                    <TableCell align="right">{stats.extendcoin || 0}</TableCell>
                    <TableCell align="right">{stats.penaltycoin || 0}</TableCell>
                    <TableCell align="right">{cnt}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Denomination Breakdown Table */}
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Denomination Breakdown</Typography>
        {Object.entries(detailed).map(([locker, det]) => (
          <Box key={locker} sx={{ mb: 4 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{locker}</Typography>
            <TableContainer component={Paper} sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Action</TableCell>
                    <TableCell>Denomination</TableCell>
                    <TableCell align="right">Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {['extend', 'penalty'].map(action => (
                    Object.entries(det[action] || {}).map(([coin, count]) => (
                      <TableRow key={locker + action + coin}>
                        <TableCell>{action.charAt(0).toUpperCase() + action.slice(1)}</TableCell>
                        <TableCell>{coin}</TableCell>
                        <TableCell align="right">{count}</TableCell>
                      </TableRow>
                    ))
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default UserManagement;